import { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { isBookOwner, isBorrower } from '../lib/roles';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  BookOpen,
  User,
  ArrowRight,
  LogIn,
  Home,
  ShieldCheck,
  Clock,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const RentalActionPage = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token: authToken } = useContext(AuthContext);

  const rawToken = params.get('token') || params.get('t') || '';
  const [currentToken, setCurrentToken] = useState(rawToken);
  const [manualInput, setManualInput] = useState('');

  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // For logged-in book owners with no token
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [declineOrderId, setDeclineOrderId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [linkReason, setLinkReason] = useState('');

  // Sync token if URL params change
  useEffect(() => {
    const t = params.get('token') || params.get('t') || '';
    setCurrentToken(t);
  }, [params]);

  // Load preview when token is present
  useEffect(() => {
    if (!currentToken) {
      setPreview(null);
      return;
    }
    setLoading(true);
    setError('');
    setResult('');
    axios
      .get(`${API}/orders/lender-action`, { params: { token: currentToken } })
      .then((res) => {
        setPreview(res.data);
      })
      .catch((e) => {
        setError(e.response?.data?.detail || 'This approval link is invalid or has expired.');
      })
      .finally(() => setLoading(false));
  }, [currentToken]);

  // If no token and user is logged in as book owner, fetch pending orders
  useEffect(() => {
    if (currentToken || !authToken || !user || !isBookOwner(user.role)) {
      return;
    }
    setLoadingOrders(true);
    axios
      .get(`${API}/owner/orders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const orders = Array.isArray(res.data) ? res.data : [];
        const pending = orders.filter(
          (o) => o.status === 'paid_pending_lender_approval' || o.lender_approval_status === 'pending'
        );
        setPendingOrders(pending);
      })
      .catch(() => {
        // Silently fail if unable to fetch
      })
      .finally(() => setLoadingOrders(false));
  }, [currentToken, authToken, user]);

  const confirmAction = async () => {
    if (!currentToken) return;
    if (isRejectAction && !linkReason.trim()) {
      toast.error('Please type a reason for declining');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/orders/lender-action`, {
        token: currentToken,
        reason: linkReason.trim(),
        refund: isRejectAction,
      });
      setResult(res.data?.message || 'Action saved successfully.');
      toast.success(res.data?.message || 'Rental request updated.');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not complete this action.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const input = manualInput.trim();
    if (!input) return;

    // Check if user pasted a full URL
    let extracted = input;
    try {
      if (input.includes('token=')) {
        const url = new URL(input.startsWith('http') ? input : `https://${input}`);
        extracted = url.searchParams.get('token') || url.searchParams.get('t') || input;
      }
    } catch {
      // Not a valid URL, treat as raw token
    }

    setParams({ token: extracted });
    setCurrentToken(extracted);
  };

  const handleOwnerDirectApprove = async (orderId) => {
    setActionInProgress(orderId);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/lender/approve`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(res.data?.message || 'Order approved successfully');
      setPendingOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve order');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleOwnerDirectReject = async (orderId) => {
    const reason = declineReason.trim();
    if (!reason) {
      toast.error('Please type a reason for declining');
      return;
    }
    setActionInProgress(orderId);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/lender/reject`,
        { reason, refund: true },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(res.data?.message || 'Order declined');
      setPendingOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline order');
    } finally {
      setActionInProgress(null);
    }
  };

  const isRejectAction = preview?.action === 'reject';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)' }}>
      <Navbar />

      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="max-w-xl w-full">
          {/* CASE 1: TOKEN PRESENT - SHOW PREVIEW & CONFIRM */}
          {currentToken && (
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Header Banner */}
              <div
                className={`p-6 text-white ${
                  isRejectAction
                    ? 'bg-gradient-to-r from-red-600 to-rose-700'
                    : 'bg-gradient-to-r from-emerald-600 to-teal-700'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {isRejectAction ? (
                    <XCircle className="h-8 w-8 text-white/90" />
                  ) : (
                    <CheckCircle2 className="h-8 w-8 text-white/90" />
                  )}
                  <div>
                    <h1 className="text-2xl font-bold">
                      {isRejectAction ? 'Decline Rental Request' : 'Approve Rental Request'}
                    </h1>
                    <p className="text-xs text-white/80 mt-0.5">
                      No login needed • Secured via signed approval link
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 sm:p-8 space-y-6">
                {loading && (
                  <div className="py-12 text-center text-gray-500">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent mb-3"></div>
                    <p className="text-sm">Verifying approval request details…</p>
                  </div>
                )}

                {error && !loading && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm text-red-800">
                      <p className="font-semibold">Unable to process link</p>
                      <p className="mt-1">{error}</p>
                      <div className="mt-3 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCurrentToken('');
                            setParams({});
                          }}
                        >
                          Try another code
                        </Button>
                        {user && isBookOwner(user.role) && (
                          <Button
                            size="sm"
                            onClick={() => navigate('/admin')}
                            style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                          >
                            Open Prosumer Dashboard
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {result && !loading && (
                  <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-4">
                    <div className="inline-flex p-3 bg-emerald-100 rounded-full text-emerald-600">
                      <CheckCircle2 className="h-8 w-8" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-emerald-900">Decision Recorded</h2>
                      <p className="text-sm text-emerald-700 mt-1">{result}</p>
                    </div>
                    <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                      {user && isBookOwner(user.role) ? (
                        <Button
                          onClick={() => navigate('/admin')}
                          style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                        >
                          Go to Prosumer Dashboard
                        </Button>
                      ) : (
                        <Button
                          onClick={() => navigate('/')}
                          style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                        >
                          Return to Homepage
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {preview && !result && !loading && (
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 space-y-3">
                      <div className="flex items-start space-x-3">
                        <BookOpen className="h-5 w-5 text-amber-700 flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Book Title</p>
                          <p className="font-bold text-gray-900 text-lg leading-snug">{preview.title}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            Borrower: <strong className="text-gray-900">{preview.borrower_name}</strong>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">
                            Rental Amount: <strong className="text-gray-900">₹{Math.round(preview.amount || 0)}</strong>
                          </span>
                        </div>
                      </div>

                      {preview.order_id && (
                        <p className="text-xs text-gray-400 font-mono">
                          Order reference: {preview.order_id}
                        </p>
                      )}
                    </div>

                    {preview.already_handled ? (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-center space-y-3">
                        <Clock className="h-6 w-6 text-amber-600 mx-auto" />
                        <p className="text-sm font-medium text-amber-900">
                          This rental request was already{' '}
                          <span className="font-bold uppercase tracking-wide">{preview.lender_approval_status}</span>.
                        </p>
                        <Button
                          onClick={() => navigate(user && isBookOwner(user.role) ? '/admin' : '/')}
                          variant="outline"
                          size="sm"
                        >
                          {user && isBookOwner(user.role) ? 'Go to Prosumer Dashboard' : 'Return to Homepage'}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs text-gray-500 text-center">
                          Click below to confirm your decision on <strong className="text-gray-700">mybook.rent</strong>.
                        </p>
                        {isRejectAction && (
                          <textarea
                            value={linkReason}
                            onChange={(e) => setLinkReason(e.target.value)}
                            placeholder="Reason for declining"
                            className="w-full min-h-[90px] rounded-xl border border-gray-300 p-3 text-sm"
                          />
                        )}
                        <Button
                          onClick={confirmAction}
                          disabled={submitting}
                          className={`w-full h-12 text-base font-semibold shadow-md transition-all ${
                            isRejectAction
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          }`}
                        >
                          {submitting ? (
                            <span className="inline-flex items-center">
                              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></span>
                              Recording decision…
                            </span>
                          ) : isRejectAction ? (
                            '✕ Decline and refund'
                          ) : (
                            '✓ Confirm: Approve Rental Request'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CASE 2: TOKEN MISSING (The user's reported state) */}
          {!currentToken && (
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Header */}
              <div
                className="p-6 text-white"
                style={{ background: 'linear-gradient(135deg, #A89968 0%, #8c7d50 100%)' }}
              >
                <div className="flex items-center space-x-3">
                  <ShieldCheck className="h-8 w-8 text-white/90" />
                  <div>
                    <h1 className="text-2xl font-bold">Lender Rental Approval</h1>
                    <p className="text-xs text-white/80 mt-0.5">
                      Confirm or decline book rental requests
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 sm:p-8 space-y-6">
                {/* 2A: LOGGED IN AS BOOK OWNER (PROSUMER / ADMIN) */}
                {user && isBookOwner(user.role) ? (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-amber-600" />
                        Welcome back, {user.name || 'Prosumer'}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Here are pending rental requests waiting for your approval:
                      </p>
                    </div>

                    {loadingOrders ? (
                      <div className="py-8 text-center text-gray-500">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-3 border-amber-600 border-t-transparent mb-2"></div>
                        <p className="text-xs">Checking for pending orders…</p>
                      </div>
                    ) : pendingOrders.length === 0 ? (
                      <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-3">
                        <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
                        <div>
                          <p className="font-semibold text-emerald-900">All caught up!</p>
                          <p className="text-xs text-emerald-700 mt-1">
                            You have no pending rental approval requests waiting right now.
                          </p>
                        </div>
                        <div className="pt-2 flex flex-col sm:flex-row gap-2 justify-center">
                          <Button
                            onClick={() => navigate('/admin')}
                            style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                          >
                            Open Prosumer Dashboard
                          </Button>
                          <Button variant="outline" onClick={() => navigate('/')}>
                            Browse Catalogue
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pendingOrders.map((order) => {
                          const item = (order.items || [])[0] || {};
                          const title = item.book_title || item.title || 'Book';
                          const isActing = actionInProgress === order.id;

                          return (
                            <div
                              key={order.id}
                              className="bg-gray-50 rounded-2xl p-5 border border-gray-200 space-y-3 shadow-sm hover:shadow transition-shadow"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start space-x-3">
                                  <BookOpen className="h-5 w-5 text-amber-700 flex-shrink-0 mt-1" />
                                  <div>
                                    <p className="font-bold text-gray-900 text-base">{title}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      Borrower: <strong className="text-gray-700">{order.customer_name || 'Borrower'}</strong>
                                    </p>
                                  </div>
                                </div>
                                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 flex-shrink-0">
                                  ₹{Math.round(order.final_amount || 0)}
                                </span>
                              </div>

                              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
                                {declineOrderId === order.id ? (
                                  <div className="w-full space-y-2">
                                    <textarea
                                      value={declineReason}
                                      onChange={(e) => setDeclineReason(e.target.value)}
                                      placeholder="Reason for declining"
                                      className="w-full min-h-[80px] rounded-xl border border-gray-300 p-3 text-sm"
                                    />
                                    <div className="flex justify-end gap-2">
                                      <Button variant="outline" size="sm" onClick={() => setDeclineOrderId(null)}>Cancel</Button>
                                      <Button size="sm" className="bg-red-600 text-white" disabled={isActing} onClick={() => handleOwnerDirectReject(order.id)}>
                                        {isActing ? 'Declining…' : 'Decline and refund'}
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={isActing}
                                  onClick={() => { setDeclineOrderId(order.id); setDeclineReason(''); }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                >
                                  Decline
                                </Button>
                                <Button
                                  size="sm"
                                  disabled={isActing}
                                  onClick={() => handleOwnerDirectApprove(order.id)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                  {isActing ? 'Processing…' : '✓ Approve'}
                                </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        <div className="text-center pt-2">
                          <Link
                            to="/admin"
                            className="text-sm font-semibold text-amber-800 hover:underline inline-flex items-center gap-1"
                          >
                            View full Prosumer Dashboard <ArrowRight className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ) : user && isBorrower(user.role) ? (
                  /* 2B: LOGGED IN AS BORROWER */
                  <div className="text-center space-y-4 py-4">
                    <div className="inline-flex p-3 bg-amber-100 rounded-full text-amber-800">
                      <BookOpen className="h-8 w-8" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Looking for your book rentals?</h2>
                      <p className="text-sm text-gray-600 mt-2 max-w-sm mx-auto">
                        This page is for book owners to approve rental requests. As a borrower, you can track your
                        active orders and delivery status in your Customer Dashboard.
                      </p>
                    </div>
                    <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        onClick={() => navigate('/customer-dashboard')}
                        style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                      >
                        Go to Customer Dashboard
                      </Button>
                      <Button variant="outline" onClick={() => navigate('/')}>
                        Browse Books
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* 2C: NOT LOGGED IN OR GENERIC VISITOR */
                  <div className="space-y-6">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-900 space-y-1">
                      <p className="font-semibold flex items-center gap-1.5">
                        <AlertCircle className="h-4 w-4 text-amber-700" />
                        No approval link detected in URL
                      </p>
                      <p className="text-xs text-amber-800 leading-relaxed">
                        Book owners receive a dedicated one-click link via WhatsApp or SMS whenever a rental order is placed.
                        If you opened this page manually, you can paste your link or code below.
                      </p>
                    </div>

                    {/* Manual Paste Form */}
                    <form onSubmit={handleManualSubmit} className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Have an approval link or code?
                      </label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Paste WhatsApp/SMS link or code..."
                          value={manualInput}
                          onChange={(e) => setManualInput(e.target.value)}
                          className="h-11"
                        />
                        <Button
                          type="submit"
                          style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                          className="h-11 px-5 flex-shrink-0"
                        >
                          Verify
                        </Button>
                      </div>
                    </form>

                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-3 text-gray-400 font-medium">Or</span>
                      </div>
                    </div>

                    {/* Log in prompt */}
                    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 text-center space-y-3">
                      <p className="text-sm font-medium text-gray-800">
                        Are you a book owner?
                      </p>
                      <p className="text-xs text-gray-500">
                        Log in to view and approve all rental requests directly in your Prosumer Dashboard.
                      </p>
                      <Button
                        onClick={() => navigate('/login?redirect=/rental-action')}
                        className="w-full h-11"
                        style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                      >
                        <LogIn className="h-4 w-4 mr-2" />
                        Log in to Prosumer Dashboard
                      </Button>
                    </div>

                    <div className="text-center">
                      <Link
                        to="/"
                        className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                      >
                        <Home className="h-3.5 w-3.5" /> Return to Homepage
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RentalActionPage;
